<script setup>
import { ref, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import gmbClient from 'dashboard/api/channel/gmbClient';

const { t } = useI18n();

const hasError = ref(false);
const errorStateMessage = ref('');
const errorStateDescription = ref('');
const isRequestingAuthorization = ref(false);

onMounted(() => {
  const urlParams = new URLSearchParams(window.location.search);
  const errorCode = urlParams.get('code');
  const errorMessage = urlParams.get('error_message');

  if (errorMessage) {
    hasError.value = true;
    if (errorCode === '400') {
      errorStateMessage.value = errorMessage;
      errorStateDescription.value = 'Authorization was cancelled or denied';
    } else if (errorCode === '404') {
      errorStateMessage.value = errorMessage;
      errorStateDescription.value = 'Please ensure you have a Google My Business location associated with your account';
    } else {
      errorStateMessage.value = 'Google My Business Connection Error';
      errorStateDescription.value = errorMessage;
    }
  }

  // Clean URL to allow retry
  const cleanURL = window.location.pathname;
  window.history.replaceState({}, document.title, cleanURL);
});

const requestAuthorization = async () => {
  isRequestingAuthorization.value = true;
  const response = await gmbClient.generateAuthorization();
  const {
    data: { url },
  } = response;

  window.location.href = url;
};
</script>

<template>
  <div class="h-full p-6 w-full max-w-full flex-shrink-0 flex-grow-0">
    <div class="flex flex-col items-center justify-start h-full text-center">
      <div v-if="hasError" class="max-w-lg mx-auto text-center">
        <h5>{{ errorStateMessage }}</h5>
        <p
          v-if="errorStateDescription"
          v-dompurify-html="errorStateDescription"
        />
      </div>
      <div
        v-else
        class="flex flex-col items-center justify-center px-8 py-10 text-center rounded-2xl outline outline-1 outline-n-weak"
      >
        <div
          class="w-20 h-20 mb-6 mx-auto rounded-full flex items-center justify-center text-white"
          style="background-color: #4285F4;"
        >
          <span class="i-ri-google-fill text-5xl" />
        </div>
        <h6 class="text-2xl font-medium">
          Connect your Google My Business
        </h6>
        <p class="py-6 text-sm text-n-slate-11">
          Connect your Google My Business to manage posts, Q&A, and reviews
        </p>
        <button
          class="flex items-center justify-center px-6 py-3 text-white rounded-full hover:shadow-lg transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
          style="background-color: #4285F4;"
          :disabled="isRequestingAuthorization"
          @click="requestAuthorization()"
        >
          <span v-if="isRequestingAuthorization" class="mr-2">
            <svg
              class="w-5 h-5 animate-spin"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                class="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                stroke-width="4"
              />
              <path
                class="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
          </span>
          <span class="i-ri-google-fill mr-2 text-xl" />
          <span class="font-medium">
            {{ isRequestingAuthorization ? 'Connecting...' : 'Continue with Google My Business' }}
          </span>
        </button>
      </div>
    </div>
  </div>
</template>
